import React from 'react';

const MySchoolBookPage = ({ onBack, userPreferences }) => {
  const getText = (textObj) => {
    return textObj[userPreferences.displayLanguage] || textObj.traditional;
  };

  const texts = {
    title: {
      traditional: '我的學校課本',
      simplified: '我的学校课本',
      english: 'My School Book'
    },
    subtitle: {
      traditional: '個人化學習記錄',
      simplified: '个性化学习记录',
      english: 'Personalized Learning Records'
    },
    description: {
      traditional: '整合學校課程內容，提供個人化的學習進度追蹤與複習功能。',
      simplified: '整合学校课程内容，提供个性化的学习进度追踪与复习功能。',
      english: 'Integrate school curriculum content, providing personalized learning progress tracking and review features.'
    },
    back: {
      traditional: '返回',
      simplified: '返回',
      english: 'Back'
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-6">
        <button
          onClick={onBack}
          className="text-blue-400 hover:text-blue-300 transition-colors"
        >
          ← {getText(texts.back)}
        </button>
        <h1 className="text-xl font-bold">{getText(texts.title)}</h1>
        <div className="w-8"></div>
      </div>

      {/* Content */}
      <div className="px-6">
        <div className="bg-gray-800/30 rounded-xl p-6 mb-6">
          <h2 className="text-2xl font-bold mb-2">{getText(texts.subtitle)}</h2>
          <p className="text-gray-300 mb-4">{getText(texts.description)}</p>
          
          {/* Placeholder content */}
          <div className="space-y-4">
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold mb-1">📚 課本管理</h3>
                  <p className="text-gray-300 text-sm">上傳和管理學校課本內容</p>
                </div>
                <div className="text-green-400 text-sm">已連接</div>
              </div>
            </div>
            
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold mb-1">📝 筆記系統</h3>
                  <p className="text-gray-300 text-sm">個人化筆記與重點標記</p>
                </div>
                <div className="text-blue-400 text-sm">12 筆記</div>
              </div>
            </div>
            
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold mb-1">🎯 進度追蹤</h3>
                  <p className="text-gray-300 text-sm">學習進度與掌握程度分析</p>
                </div>
                <div className="text-yellow-400 text-sm">75%</div>
              </div>
            </div>
            
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold mb-1">🔄 複習提醒</h3>
                  <p className="text-gray-300 text-sm">智能複習計劃與提醒</p>
                </div>
                <div className="text-purple-400 text-sm">3 待複習</div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-gray-800/30 rounded-xl p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">📅 最近活動</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-gray-300 text-sm">完成 Chapter 5 語法練習</span>
              <span className="text-gray-500 text-xs ml-auto">2小時前</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span className="text-gray-300 text-sm">新增 3 個重點筆記</span>
              <span className="text-gray-500 text-xs ml-auto">昨天</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
              <span className="text-gray-300 text-sm">複習 Chapter 3 內容</span>
              <span className="text-gray-500 text-xs ml-auto">3天前</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-gray-800/30 rounded-xl p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">⚡ 快速操作</h3>
          <div className="grid grid-cols-2 gap-3">
            <button className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-3 text-center hover:bg-blue-500/30 transition-colors">
              <div className="text-2xl mb-1">📖</div>
              <div className="text-sm">開始學習</div>
            </button>
            <button className="bg-green-500/20 border border-green-500/50 rounded-lg p-3 text-center hover:bg-green-500/30 transition-colors">
              <div className="text-2xl mb-1">📝</div>
              <div className="text-sm">新增筆記</div>
            </button>
            <button className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 text-center hover:bg-yellow-500/30 transition-colors">
              <div className="text-2xl mb-1">🔄</div>
              <div className="text-sm">複習模式</div>
            </button>
            <button className="bg-purple-500/20 border border-purple-500/50 rounded-lg p-3 text-center hover:bg-purple-500/30 transition-colors">
              <div className="text-2xl mb-1">📊</div>
              <div className="text-sm">進度報告</div>
            </button>
          </div>
        </div>

        {/* Coming Soon Notice */}
        <div className="text-center py-8">
          <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-2">🚧 開發中</h3>
            <p className="text-gray-300">
              {userPreferences.displayLanguage === 'traditional' ? '此功能正在開發中，敬請期待！' :
               userPreferences.displayLanguage === 'simplified' ? '此功能正在开发中，敬请期待！' :
               'This feature is under development. Stay tuned!'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MySchoolBookPage;
